/*=========================================================================================
  File Name: router.js
  Description: Routes for vue-router. Lazy loading is enabled.
  ----------------------------------------------------------------------------------------
  Item Name: Vuexy - Vuejs, HTML & Laravel Admin Dashboard Template
  Author: Pixinvent
  Author URL: http://www.themeforest.net/user/pixinvent
==========================================================================================*/


import Vue from 'vue'
import Router from 'vue-router'
import store from "./store/store";
import {Role} from "./enums/role";

Vue.use(Router)

const router = new Router({
    base: process.env.BASE_URL,
    scrollBehavior() {
        return {x: 0, y: 0}
    },
    routes: [

        {
            // =============================================================================
            // MAIN LAYOUT ROUTES
            // =============================================================================
            path: '/user',
            component: () => import('./layouts/main/Main.vue'),
            children: [
                // =============================================================================
                // Theme Routes
                // =============================================================================
                {
                    path: '',
                    name: 'user-view',
                    component: () => import('./views/UserView.vue'),
                    beforeEnter: (to, from, next) => {
                        if (!store.state.AppActiveUser.membership.valid) {
                            next({name: 'invalid-membership'});
                        } else {
                            next();
                        }
                    },
                    meta: {authorize: [Role.User]}
                },
                {
                    path: 'activity',
                    name: 'transactions-view',
                    component: () => import('./views/TransactionsView.vue'),
                    beforeEnter: (to, from, next) => {
                        if (!store.state.AppActiveUser.membership.valid) {
                            next({name: 'invalid-membership'});
                        } else {
                            next();
                        }
                    },
                    meta: {authorize: [Role.User]}
                },
                {
                    path: 'downline',
                    name: 'descendant-view',
                    component: () => import('./views/DescendantView.vue'),
                    beforeEnter: (to, from, next) => {
                        if (!store.state.AppActiveUser.membership.valid) {
                            next({name: 'invalid-membership'});
                        } else {
                            next();
                        }
                    },
                    meta: {authorize: [Role.User]}
                },
                {
                    path: 'profile',
                    name: 'profile',
                    component: () => import('./views/Profile.vue'),
                    beforeEnter: (to, from, next) => {
                        if (!store.state.AppActiveUser.membership.valid) {
                            next({name: 'invalid-membership'});
                        } else {
                            next();
                        }
                    },
                    meta: {authorize: [Role.User]}
                },
                {
                    path: 'mlm-bonus',
                    name: 'tree',
                    component: () => import('./views/Tree.vue'),
                    beforeEnter: (to, from, next) => {
                        if (!store.state.AppActiveUser.membership.valid) {
                            next({name: 'invalid-membership'});
                        } else {
                            next();
                        }
                    },
                    meta: {authorize: [Role.User]}
                },
                {
                    path: 'withdrawal',
                    name: 'withdrawal',
                    component: () => import('./views/Withdrawal.vue'),
                    beforeEnter: (to, from, next) => {
                        if (!store.state.AppActiveUser.membership.valid) {
                            next({name: 'invalid-membership'});
                        } else {
                            next();
                        }
                    },
                    meta: {authorize: [Role.User]}
                },
                {
                    path: '*',
                    redirect: {name: 'user-view'}
                }
            ],
        },
        {
            path: '/admin',
            component: () => import('./layouts/main/Main.vue'),
            children: [
                {
                    path: '',
                    name: 'admin-view',
                    component: () => import('./views/AdminView.vue'),
                    meta: {authorize: [Role.Admin]},
                },
                {
                    path: 'activity',
                    name: 'transactions-admin',
                    component: () => import('./views/TransactionsAdmin.vue'),
                    meta: {authorize: [Role.Admin]},
                },
                {
                    path: 'withdrawal',
                    name: 'withdrawal-admin',
                    component: () => import('./views/WithdrawalAdmin.vue'),
                    meta: {authorize: [Role.Admin]},
                },
                {
                    path: 'profile',
                    name: 'profile-admin',
                    component: () => import('./views/Profile.vue'),
                    meta: {authorize: [Role.Admin]}
                },
                {
                    path: 'members',
                    name: 'members-list',
                    component: () => import('./views/MembersList.vue'),
                    meta: {authorize: [Role.Admin]}
                },
                {
                    path: 'members/user',
                    name: 'members-referrals-admin-list',
                    component: () => import('./views/MembersReferralsList.vue'),
                    meta: {authorize: [Role.Admin]}
                },
                {
                    path: 'members/referrals',
                    name: 'members-referrals',
                    component: () => import('./views/MembersView.vue'),
                    meta: {authorize: [Role.Admin]}
                },
            ]
        },
        // =============================================================================
        // FULL PAGE LAYOUTS
        // =============================================================================
        {
            path: '',
            component: () => import('@/layouts/full-page/FullPage.vue'),
            children: [
                // =============================================================================
                // PAGES
                // =============================================================================
                {
                    path: '/pages/login',
                    name: 'page-login',
                    component: () => import('@/views/pages/Login.vue')
                },
                {
                    path: '/pages/error-404',
                    name: 'page-error-404',
                    component: () => import('@/views/pages/Error404.vue')
                },
                {
                    path: '/invalid-membership',
                    name: 'invalid-membership',
                    component: () => import('./views/InvalidMembership.vue'),
                    beforeEnter: (to, from, next) => {
                        if (store.state.AppActiveUser.membership.valid) {
                            return next({
                                name: store.state.AppActiveUser.userRole + '-view'
                            });
                        } else {
                            return next();
                        }
                    }
                }
            ]
        },
        // Redirect to 404 page, if no match found
        {
            path: '*',
            redirect: '/pages/error-404'
        }
    ],
});

router.beforeEach(async (to, from, next) => {
    if (to.meta.authorize) {
        if (store.state.AppActiveUser.userRole && !to.meta.authorize.includes(store.state.AppActiveUser.userRole)) {
            return next({
                name: store.state.AppActiveUser.userRole + '-view'
            });
        } else {
            if (store.state.AppActiveUser.userRole) {
                if (store.state.AppActiveUser.membership.valid) {
                    if (!to.path.includes(store.state.AppActiveUser.userRole)) {
                        return next({
                            name: store.state.AppActiveUser.userRole + '-view'
                        });
                    } else {
                        return next();
                    }
                } else {
                    if (to.path !== '/invalid-membership') {
                        return next({name: 'invalid-membership'});
                    } else {
                        next();
                    }
                }
            } else {
                return next({name: 'page-error-404'});
            }
        }
    } else {
        if (store.state.AppActiveUser.membership.valid) {
            return next({
                name: store.state.AppActiveUser.userRole + '-view'
            });
        } else {
            if (to.path !== '/invalid-membership') {
                return next({name: 'invalid-membership'});
            } else {
                next();
            }
        }
    }
});


router.afterEach(() => {
    // Remove initial loading
    const appLoading = document.getElementById('loading-bg');
    if (appLoading) {
        appLoading.style.display = "none";
    }
});

export default router
